// ─── programCompleteness.js ─────────────────────────────────────────────
// Refonte écran Suivi — Phase 1 (socle). Voir docs/PLAN-REFONTE-ECRAN-SUIVI.md.
//
// Fonction PURE : à partir d'une cliente + sa consultation, renvoie l'état de
// complétude des 7 pièces du programme (plan, lettre d'intro, stratégie,
// recettes, fiche frigo, compléments, phases). C'est la CLÉ DE VOÛTE de la
// refonte : le module de génération, la barrière de publication et l'alerte
// miroir consomment tous cette même vérité.
//
// Source de vérité = le plan assemblé par buildClientAppPlanFromConsultation
// (exactement ce que la cliente reçoit) + les marqueurs bruts stockés sur la
// consultation (intro_letter, meal_recipes, fiche_frigo_json, editorial_
// overrides, protocol_phases). On NE re-parse rien à la main.
//
// Ne wire rien, ne publie rien, ne modifie aucun état : lecture seule.

import { buildClientAppPlanFromConsultation } from './clientAppMapper';

export const PIECE_STATUS = Object.freeze({
  READY: 'pret',           // pièce prête
  PARTIAL: 'partiel',      // partiellement générée (ex. recettes 4/12)
  TODO: 'a_generer',       // à générer / compléter
  NA: 'non_applicable',    // pas encore pertinent (ex. pas de plan)
});

const { READY, PARTIAL, TODO, NA } = PIECE_STATUS;

// Ordre d'affichage figé (miroir de la maquette du module de génération).
const PIECE_LABELS = Object.freeze([
  ['plan', 'Plan nutrition'],
  ['intro', "Lettre d'intro"],
  ['strategy', 'Stratégie'],
  ['recipes', 'Recettes'],
  ['fridge', 'Fiche frigo'],
  ['supplements', 'Compléments'],
  ['phases', 'Phases du parcours'],
]);

/** Vrai si la valeur porte un contenu réel (string non vide, tableau/objet non vides). */
function hasContent(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number') return true;
  if (typeof v === 'boolean') return v === true;
  if (Array.isArray(v)) return v.some(hasContent);
  if (typeof v === 'object') return Object.values(v).some(hasContent);
  return false;
}

// Champs d'en-tête (chrome) présents même quand une section est vide : on ne
// veut PAS qu'un simple titre fasse passer une section pour « prête ».
const CHROME_KEYS = new Set([
  'header_title', 'title', 'label', 'locale', 'type', 'id', 'key', 'order', 'status',
]);

/**
 * Vrai si une section du plan assemblé porte un CORPS (pas seulement son
 * en-tête). Cherche d'abord des champs porteurs de contenu classiques, sinon
 * n'importe quel champ non-chrome non vide.
 */
function sectionHasBody(section) {
  if (section == null) return false;
  if (typeof section !== 'object' || Array.isArray(section)) return hasContent(section);
  const bodyKeys = [
    'items', 'pillars', 'list', 'lines', 'paragraphs', 'entries', 'days',
    'rows', 'content', 'body', 'blocks', 'groups', 'categories', 'supplements', 'foods',
  ];
  for (const k of bodyKeys) if (hasContent(section[k])) return true;
  return Object.entries(section).some(([k, v]) => !CHROME_KEYS.has(k) && hasContent(v));
}

/** Texte du plan, tolérant aux conventions snake/camel (cf. clientAppMapper). */
function resolvePlanText(consultation) {
  return String(
    consultation?.nutrition_plan
    || consultation?.nutritionPlan
    || consultation?.plan_text
    || '',
  ).trim();
}

function piece(key, status, detail) {
  const label = PIECE_LABELS.find(([k]) => k === key)?.[1] || key;
  return { key, label, status, detail: detail || '' };
}

function finalize(pieces) {
  const applicable = pieces.filter((p) => p.status !== NA);
  const readyCount = pieces.filter((p) => p.status === READY).length;
  const allReady = applicable.length > 0 && applicable.every((p) => p.status === READY);
  const missing = applicable.filter((p) => p.status !== READY).map((p) => p.key);
  return {
    pieces,
    readyCount,
    applicableCount: applicable.length,
    allReady,
    missing,
  };
}

/**
 * Compte les menus réellement visibles dans l'application, une seule fois par
 * couple slot/titre. Les sept jours répètent volontairement la semaine type :
 * ils ne doivent donc pas gonfler le total. Les alternatives font partie du
 * contrat publié et sont comptées avec leur recette attachée par le mapper.
 */
export function countMappedRecipeCoverage(weekMeals) {
  const seen = new Set();
  let total = 0;
  let covered = 0;
  const visit = (slot, candidate) => {
    const title = String(candidate?.title || '').trim();
    if (!title) return;
    const key = `${String(slot || '').trim()}::${title.toLocaleLowerCase('fr-CH')}`;
    if (seen.has(key)) return;
    seen.add(key);
    total += 1;
    if (hasContent(candidate?.recipe)) covered += 1;
  };
  for (const day of Array.isArray(weekMeals?.days) ? weekMeals.days : []) {
    for (const meal of Array.isArray(day?.meals) ? day.meals : []) {
      visit(meal?.slot, meal);
      for (const alternative of Array.isArray(meal?.alternatives) ? meal.alternatives : []) {
        visit(meal?.slot, alternative);
      }
    }
  }
  return Object.freeze({ covered, total });
}

/**
 * Décide uniquement quel workspace afficher dans « Maintenant ».
 * Un programme déjà en direct ne doit jamais réafficher le générateur à cause
 * d'un ancien compteur éditorial. Un vrai brouillon en attente reste prioritaire.
 */
export function shouldShowProgramGeneration({
  phaseTransitionRunning = false,
  phaseTransitionDraftReady = false,
  programIsLive = false,
  allReady = false,
  clientAppPending = false,
  documentRequired = false,
  documentConfirmed = false,
} = {}) {
  if (phaseTransitionRunning || phaseTransitionDraftReady) return false;
  if (clientAppPending) return true;
  if (programIsLive) return false;
  return !allReady || (documentRequired && !documentConfirmed);
}

/**
 * Calcule la complétude du programme d'une cliente.
 *
 * @param {object|null} client
 * @param {object|null} consultation
 * @returns {{
 *   pieces: Array<{ key:string, label:string, status:string, detail:string }>,
 *   readyCount: number,
 *   applicableCount: number,
 *   allReady: boolean,
 *   missing: string[],
 * }}
 */
export function computeProgramCompleteness(client, consultation) {
  const planText = resolvePlanText(consultation);

  // Sans plan : rien à publier encore. Le plan est « à générer », le reste
  // n'est pas encore pertinent (jamais « à générer » à vide → pas de fausse
  // alarme, pas de fausse complétude).
  if (!client || !consultation || !planText) {
    return finalize(
      PIECE_LABELS.map(([key]) =>
        key === 'plan'
          ? piece('plan', TODO, 'À rédiger dans l\'onglet Plan')
          : piece(key, NA, 'En attente du plan'),
      ),
    );
  }

  // Plan assemblé = ce que la cliente recevra. Fail-safe : si le mapper throw
  // (donnée partielle), on retombe sur des sections vides (tout « à générer »).
  let plan = null;
  try {
    plan = buildClientAppPlanFromConsultation(client, consultation);
  } catch {
    plan = null;
  }
  const sections = plan?.sections || {};

  // Recettes : ratio calculé sur le DTO exact de l'application. Cela garde les
  // conventions suisses, les titres édités et les alternatives validées sans
  // reparsing concurrent du Markdown.
  const { total: totalMeals, covered } = countMappedRecipeCoverage(sections.week_meals);
  const recipesStatus = totalMeals === 0
    ? NA
    : covered === 0
      ? TODO
      : covered < totalMeals ? PARTIAL : READY;

  // Marqueurs par pièce : storage dédié OU corps de section assemblée.
  // Une salutation/signature de fallback n'est pas une lettre. Seul un corps
  // narratif réellement publié rend l'introduction prête.
  const introReady = hasContent(sections.intro_data?.body);
  const strategyReady = hasContent(consultation?.editorial_overrides?.strategy) || sectionHasBody(sections.strategy_data);
  const fridgeReady = hasContent(consultation?.fiche_frigo_json)
    || hasContent(consultation?.editorial_overrides?.fridge)
    || sectionHasBody(sections.fridge_data);
  const supplementsReady = hasContent(consultation?.editorial_overrides?.supplements_intro)
    || sectionHasBody(sections.protocols_data);

  // Les phases sont une extension facultative. Leur absence ne doit pas
  // empêcher la livraison d'un programme complet ; si elles existent, elles
  // restent publiées et contrôlées par leur propre contrat.
  const phasesSkipped = consultation?.protocol_phases?.skipped === true;
  const phasesReady = phasesSkipped || hasContent(plan?.journey_phases);

  return finalize([
    piece('plan', READY, ''),
    piece('intro', introReady ? READY : TODO, introReady ? '' : 'À générer'),
    piece('strategy', strategyReady ? READY : TODO, strategyReady ? '' : 'À générer'),
    piece(
      'recipes',
      recipesStatus,
      totalMeals === 0 ? 'Aucun repas détecté' : `${covered} / ${totalMeals} générées`,
    ),
    piece('fridge', fridgeReady ? READY : TODO, fridgeReady ? '' : 'À compléter'),
    piece('supplements', supplementsReady ? READY : TODO, supplementsReady ? '' : 'À compléter'),
    piece(
      'phases',
      phasesReady ? READY : NA,
      phasesSkipped ? 'Parcours désactivé' : phasesReady ? '' : 'Optionnel',
    ),
  ]);
}
